import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import { ClientsOptions } from "@/redux/slices/client/types";
import { createSearchParams } from "@/utils";
import { transformClientOptions, transformClients } from "./helpers";
import {
  GetClientsParams,
  GetClientsResponse,
  ReactivateAccountResponse,
  RemoveAccessResponse,
  BulkInviteClientParams,
  BulkInviteClientResponse,
  CancelClientInviteResponse,
} from "./types";

export const clientApi = createApi({
  reducerPath: "clientApi",
  baseQuery: baseQueryWithProvider("client"),
  endpoints: (builder) => ({
    getClients: builder.query<GetClientsResponse, GetClientsParams>({
      query: (params) => `?${createSearchParams(params).toString()}`,
      providesTags: ["Clients"],
      transformResponse: transformClients,
    }),
    removeAccess: builder.mutation<RemoveAccessResponse, string>({
      query: (id) => ({
        url: `/${id}/access`,
        method: "DELETE",
      }),
      invalidatesTags: ["Clients"],
    }),
    reactivateAccount: builder.mutation<ReactivateAccountResponse, string>({
      query: (id) => ({
        url: `/${id}/access`,
        method: "POST",
      }),
      invalidatesTags: ["Clients"],
    }),
    getClientOptions: builder.query<ClientsOptions, void>({
      query: () => "/options",
      transformResponse: transformClientOptions,
    }),
  }),
  tagTypes: ["Clients"],
});

export const {
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useRemoveAccessMutation,
  useReactivateAccountMutation,
  useGetClientOptionsQuery,
} = clientApi;

// Separate API for bulk invite since it uses different base URL pattern
export const bulkInviteClientApi = createApi({
  reducerPath: "bulkInviteClientApi",
  baseQuery: baseQueryWithProvider("bulk-invite"),
  endpoints: (builder) => ({
    bulkInviteClient: builder.mutation<BulkInviteClientResponse, BulkInviteClientParams>({
      query: (body) => ({
        url: "/client",
        method: "POST",
        body,
      }),
      // Tags don't cross createApi instances, so refresh the clients list (in
      // clientApi) once the invite succeeds — the new pending client appears.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(["Clients"]));
      },
    }),
    // Re-send a pending client's invite email. Backend only re-fires the email —
    // no account/Kinde/invite is recreated — so the client stays pending and the
    // list doesn't need refreshing.
    resendClientInvite: builder.mutation<void, string>({
      query: (accountUuid) => ({
        url: `/client/${accountUuid}/resend`,
        method: "POST",
      }),
    }),
    // Cancel (revoke) a pending client's invitation. The backend removes the pending
    // relationship so the client drops off the Pending tab and the same email can be
    // invited again — so refresh the clients list once it succeeds.
    cancelClientInvite: builder.mutation<CancelClientInviteResponse, string>({
      query: (accountUuid) => ({
        url: `/client/${accountUuid}/cancel`,
        method: "POST",
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(clientApi.util.invalidateTags(["Clients"]));
      },
    }),
  }),
});

export const { useBulkInviteClientMutation, useResendClientInviteMutation, useCancelClientInviteMutation } = bulkInviteClientApi;
